import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined as LockIcon,
  VerifiedUserOutlined as VerifyIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

export default function ClaimProfile() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { claimProfile } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [instructorInfo, setInstructorInfo] = useState<{ firstName: string; lastName: string; email: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Claim Your Driving Instructor Profile - Adilesson";

    const fetchClaimInfo = async () => {
      if (!token) {
        setError("Claim token is missing. Please check your verification link.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const res = await api.getClaimInfo(token);
        if (res.data) {
          setInstructorInfo(res.data);
        }
      } catch (err: any) {
        console.error("Failed to load claim details:", err);
        let msg = "Invalid or expired claim token. Please contact support.";
        if (err.response?.data?.message) {
          msg = err.response.data.message;
        }
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchClaimInfo();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!token) return;

    if (!password || !confirmPassword) {
      setFormError("Both password fields are required.");
      return;
    }

    if (password.length < 6) {
      setFormError("Password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    try {
      setSubmitting(true);
      await claimProfile(token, password, confirmPassword);
      // useAuth claimProfile will automatically save token and navigate to dashboard
    } catch (err: any) {
      console.error("Claim profile failed:", err);
      let msg = "Failed to claim profile. Please try again.";
      if (err.response?.data?.message) {
        msg = err.response.data.message;
      } else if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        msg = Array.isArray(errors) ? errors.join(" ") : Object.values(errors).flat().join(" ");
      }
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper 
          elevation={4} 
          sx={{ 
            p: 4, 
            borderRadius: 3, 
            width: '100%',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(226, 232, 240, 0.8)'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <VerifyIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" component="h1" fontWeight="700" color="text.primary">
              Claim Your Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" mt={1}>
              Secure your imported driving instructor listing and set up your takvim.
            </Typography>
          </Box>

          {error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : (
            <>
              {instructorInfo && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="600">
                    E-posta Doğrulandı:
                  </Typography>
                  <Typography variant="body2">
                    {instructorInfo.firstName} {instructorInfo.lastName} ({instructorInfo.email})
                  </Typography>
                </Alert>
              )}

              {formError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {formError}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />

                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting}
                  sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 2, fontWeight: '600' }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : 'Set Password & Activate Profile'}
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
